import { CSSProperties, ReactNode } from 'react';
import clsx from 'clsx';
import { IDataTableColumn } from '@srm/ui/data-table';
import DisplayField from '@molecules/DisplayField/DisplayField';
import UserName from '@molecules/UserName';
import { ValueType } from '@srm/ui/types/ValueType';
import { fileType } from '@srm/ui/lib/fileTypeInfo';
import { IAttachColumn, IAttachRow } from './AttachGrid.types';
import { extOf, primaryColumn, publicationInfo, rowFileName, rowValue, userKeyOf } from './attachRowView';
import { humanizeDateTime } from './attachDate';

/** Классификация колонок схемы вложений. */
export const isPublicationColumn = (c: IAttachColumn): boolean => c.isStatusColumn || /Publication/i.test(c.field);
export const isVersionColumn = (c: IAttachColumn): boolean => /Version/i.test(c.field);
const isPersonColumn = (c: IAttachColumn): boolean => !!c.userKeyField || c.dataType === ValueType.AddressBook;
const isDateColumn = (c: IAttachColumn): boolean =>
    c.dataType === ValueType.Date || c.dataType === ValueType.DateTime || c.dataType === ValueType.NoSecDateTime;
const isNumericColumn = (c: IAttachColumn): boolean => c.filterKind === 'number' || c.dataType === ValueType.FileSize;

/** Ширины треков: имя файла тянется, служебные колонки фиксированы. */
const widthOf = (c: IAttachColumn, primary: boolean): Pick<IDataTableColumn<IAttachRow>, 'width' | 'flexWeight'> => {
    if (primary) return { flexWeight: 24 };
    if (isVersionColumn(c)) return { width: 90 };
    if (isPublicationColumn(c)) return { width: 140 };
    if (isPersonColumn(c)) return { flexWeight: 13 };
    if (c.dataType === ValueType.FileSize) return { width: 108 };
    if (isDateColumn(c)) return { width: 150 };
    return { flexWeight: 10 };
};

/** Номер текущей версии из «x/y». */
const versionCurrent = (value: unknown): string => {
    const s = value == null ? '' : String(value);
    return s.split('/')[0] || '—';
};

interface IAttachColumnsDeps {
    /** Режим чтения: без ссылок-действий и hover-карточек пользователей. */
    readOnly: boolean;
    /** Идёт выбор строк — клик по имени файла не должен скачивать. Спрашивается в момент клика:
     *  первая и последняя отметка не пересобирают колонки и не сбрасывают кэш строк таблицы. */
    isSelectionActive: () => boolean;
    onDownload?: (row: IAttachRow) => void;
}

const cellContent = (column: IAttachColumn, row: IAttachRow, deps: IAttachColumnsDeps): ReactNode => {
    const value = rowValue(row, column.field);
    if (isPublicationColumn(column)) {
        const { text, published } = publicationInfo(value);
        return (
            <span className={clsx('atb__status', published ? 'atb__status--yes' : 'atb__status--no')}>
                {text || '—'}
            </span>
        );
    }
    if (isPersonColumn(column)) {
        const name = value == null ? '' : String(value);
        if (!name) return <span className="atb__cell-muted">—</span>;
        if (deps.readOnly) return <span>{name}</span>;
        return <UserName userKey={userKeyOf(column, row)} name={name} withIcon avatar />;
    }
    if (isVersionColumn(column)) return <span className="atb__ver">{versionCurrent(value)}</span>;
    if (isDateColumn(column)) return <span className="atb__cell-num">{humanizeDateTime(value)}</span>;
    return (
        <DisplayField
            type={column.dataType}
            value={value as never}
            className={isNumericColumn(column) ? 'atb__cell-num' : undefined}
        />
    );
};

/** Текстовое представление ячейки: то же, что видит пользователь (статус публикации,
 *  номер версии, ФИО, человекочитаемая дата). */
export const cellPlainText = (column: IAttachColumn, row: IAttachRow): string => {
    const value = rowValue(row, column.field);
    if (isPublicationColumn(column)) return publicationInfo(value).text || '';
    if (isVersionColumn(column)) return versionCurrent(value);
    if (isDateColumn(column)) return humanizeDateTime(value);
    return value == null ? '' : String(value);
};

/** Ячейка имени файла: иконка типа, само имя (кликом — скачивание) и подпись формата. */
const fileCell = (row: IAttachRow, primaryField: string | undefined, deps: IAttachColumnsDeps): ReactNode => {
    const name = rowFileName(row, primaryField);
    const type = fileType(extOf(name));
    const Icon = type.Icon;
    return (
        <div
            className="atb__file"
            style={
                {
                    '--tile-accent': type.accent,
                    '--tile-accent-soft': type.accentSoft,
                    '--tile-accent-ink': type.accentInk,
                } as CSSProperties
            }
        >
            <span className="atb__mark">
                <Icon aria-hidden />
            </span>
            <span className="atb__file-text">
                {deps.readOnly ? (
                    <span className="atb__name" title={name}>
                        {name}
                    </span>
                ) : (
                    <button
                        type="button"
                        className="atb__name"
                        title={name}
                        onClick={() => {
                            if (!deps.isSelectionActive()) deps.onDownload?.(row);
                        }}
                    >
                        {name}
                    </button>
                )}
                <span className="atb__sub">
                    <span className="atb__sub-ext">{type.label}</span>
                </span>
            </span>
        </div>
    );
};

/**
 * Колонки схемы вложений в модель базового грида. Рендеры перенесены один в один
 * из прежней таблицы, поэтому вид строки не меняется — меняется только движок
 * (сортировка, фильтры, пагинация и виртуализация переезжают в @atoms/DataTable).
 */
export function buildAttachColumns(columns: IAttachColumn[], deps: IAttachColumnsDeps): IDataTableColumn<IAttachRow>[] {
    const primary = primaryColumn(columns);
    return columns
        .filter((column) => column.show)
        .map((column) => {
            const isPrimary = column.field === primary?.field;
            return {
                field: column.field,
                header: column.header,
                accessor: (row: IAttachRow) => rowValue(row, column.field),
                dataType: column.dataType,
                // числа и даты вправо, версия по
                // центру, остальное влево (alignment схемы для них не показателен)
                alignment:
                    isNumericColumn(column) || isDateColumn(column)
                        ? 'right'
                        : isVersionColumn(column)
                          ? 'center'
                          : 'left',
                allowSorting: column.allowSorting,
                allowFiltering: column.allowFiltering,
                allowHiding: column.showInColumnChooser,
                visible: column.show,
                headerNoEllipsis: true,
                // header-фильтр и выгрузка показывают тот же текст, что и ячейка
                cellText: (row: IAttachRow) =>
                    isPrimary ? rowFileName(row, primary?.field) : cellPlainText(column, row),
                ...widthOf(column, isPrimary),
                render: (row: IAttachRow) =>
                    isPrimary ? fileCell(row, primary?.field, deps) : cellContent(column, row, deps),
            } satisfies IDataTableColumn<IAttachRow>;
        });
}
